import { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { cloneDeep } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { dialogFunction } from 'common/components';

import {
  createFormulaColDef,
  createFormulaActiveIngredientColDef,
} from '../utils/gridColumnUtils';
import { getAllRows } from '../utils';

import { useAsync } from 'hooks';
import { useSelectProductFullDetailData } from 'pages/qa-spec/hooks';

import { getDataFormula } from 'services/product';

export const useGetDataFormula = () => {
  const { data, setData, run } = useAsync();
  const { id: productId } = useParams();

  const fetchDataFormula = useCallback(
    (productId) => {
      run(getDataFormula({ productId }));
    },
    [run]
  );

  useEffect(() => {
    if (productId) {
      fetchDataFormula(productId);
    }
    return;
  }, [productId, run, fetchDataFormula]);

  const formatedData = useMemo(() => {
    return {
      ...(data || {}),
      formula: data?.formula?.map((item) => {
        return { ...item, uuid: uuidv4() };
      }),
    };
  }, [JSON.stringify(data)]);

  return {
    data: formatedData,
    setData,
    fetchDataFormula,
  };
};

export const useGetFormulaColDefs = (productEnums, productId, options) => {
  const productDetail = useSelectProductFullDetailData({ productId });

  const formulaColDef = useMemo(() => {
    const { productId, ownerId: productOwnerId } = productDetail;
    return createFormulaColDef({
      productEnums,
      productOwnerId,
      productId,
      options,
    });
  }, [
    JSON.stringify(productEnums),
    JSON.stringify(productDetail),
    JSON.stringify(options),
  ]);

  return formulaColDef;
};

export const useGetFormulaGridHandlers = ({
  gridInst,
  displayNameMaps,
  qasRequiredFieldMessage,
}) => {
  const handleAddFormula = () => {
    const newUuid = uuidv4();
    const allRowData = getAllRows(gridInst);
    const newRowIndex = allRowData?.length;

    gridInst.current.api.applyTransaction({
      add: [
        {
          uuid: newUuid,
          salesDesignation: null,
          technicalDesignation: null,
          casNumber: null,
          function: null,
          classificationAndRiskPhrases: null,
          origin: null,
          originSpecification: null,
          country: null,
          percentOfRawMaterialSubstance: null,
          percentOfIncorporatedRawMaterial: null,
          percentOfFinishedProductSubstance: null,
          documents: null,
          error: [...qasRequiredFieldMessage],
        },
      ],
      addIndex: newRowIndex,
    });

    gridInst.current.api.ensureIndexVisible(newRowIndex);
    const newRow = gridInst.current.api.getRowNode(newUuid);

    newRow.setSelected(true, true);
  };

  const onDeleteFormula = async () => {
    const selectedRows = gridInst.current.api.getSelectedNodes();

    const removeList = selectedRows.map(
      (selectedRowItem) => selectedRowItem?.data
    );

    gridInst.current.api.applyTransaction({
      remove: removeList,
    });
  };

  const confirmDeleteFormula = () => {
    dialogFunction({
      type: 'warn',
      content: 'Are you sure you want to delete the selected item?',
      okText: 'OK',
      cancelText: 'Cancel',
      onOk: onDeleteFormula,
    });
  };

  const handleDeleteFormula = () => {
    confirmDeleteFormula();
  };

  const validateQasRequiredFields = (rowData) => {
    let errors = [];

    Object.keys(rowData).forEach((fieldName) => {
      const value = rowData[fieldName];

      const displayName = displayNameMaps[fieldName];
      const errorMsg = (displayName || fieldName) + ' is required';

      const isRequired = qasRequiredFieldMessage.includes(errorMsg);

      if (isRequired && !value) {
        errors = [...new Set([...errors, errorMsg])];
      } else {
        errors = errors.filter((errorItem) => errorItem !== errorMsg);
      }
    });

    return errors;
  };

  const validateAllRow = () => {
    const allRowData = getAllRows(gridInst);

    const validatedRowData = allRowData.map((rowDataItem) => {
      let errors = [];

      if (qasRequiredFieldMessage?.length) {
        const qasRequiredErrors = validateQasRequiredFields(rowDataItem);

        errors = [...new Set([...errors, ...qasRequiredErrors])];
      }

      return { ...rowDataItem, error: errors };
    });

    gridInst.current.api.applyTransaction({
      update: cloneDeep(validatedRowData),
    });
  };

  const checkGridError = () => {
    const allRowData = getAllRows(gridInst);

    return allRowData.some((rowDataItem) => rowDataItem?.error?.length > 0);
  };

  return {
    handleAddFormula,
    handleDeleteFormula,
    validateAllRow,
    checkGridError,
  };
};

export const useGetFormulaActiveIngredientColDefs = (options) => {
  const formulaColDef = useMemo(() => {
    return createFormulaActiveIngredientColDef(options);
  }, [JSON.stringify(options)]);

  return formulaColDef;
};

export const useFormulaActiveIngredientHandler = (gridInst) => {
  const handleAddActiveIngredient = () => {
    const newUuid = uuidv4();
    const allRowData = getAllRows(gridInst);
    const newRowIndex = allRowData?.length;

    gridInst.current.api.applyTransaction({
      add: [
        {
          uuid: newUuid,
          ingredientName: null,
          purposeDescriptor: null,
          quantity: null,
          quantityUom: 'g',
          error: ['Ingredient Name is required'],
        },
      ],
      addIndex: newRowIndex,
    });

    gridInst.current.api.ensureIndexVisible(newRowIndex);
    const newRow = gridInst.current.api.getRowNode(newUuid);

    newRow.setSelected(true, true);
  };

  const onDeleteIngredient = async () => {
    const selectedRows = gridInst.current.api.getSelectedNodes();

    const removeList = selectedRows.map(
      (selectedRowItem) => selectedRowItem?.data
    );

    gridInst.current.api.applyTransaction({
      remove: removeList,
    });
  };

  const confirmDeleteIngredient = () => {
    dialogFunction({
      type: 'warn',
      content: 'Are you sure you want to delete the selected item?',
      okText: 'OK',
      cancelText: 'Cancel',
      onOk: onDeleteIngredient,
    });
  };

  const handleDeleteIngredient = () => {
    confirmDeleteIngredient();
  };

  return {
    handleAddActiveIngredient,
    handleDeleteIngredient,
  };
};
